import React, { useState } from "react";
import { useGetOffersListNewQuery } from "../Service/extendedOffersApi";
import { Offer, OfferCloneSection, OfferSearchFilters } from "../Types/offerTypes";
import DataTable, { ColumnConfig } from "../../Shared/Components/DataTable";

import { useCreateRedirect } from '../../Shared/Hooks/useCreateRedirect';
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import ActionButtons from "../../Shared/Components/ActionButtons";
import { useDeleteOfferMutation } from "../Service/offersApi";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { OffersRoutes } from "../../../Router/path";
import OfferSearchForm from "../Components/OfferSearch/OfferSearchForm ";
import CopyOfferSectionsDialog from "../Components/CopyOfferSectionsDialog/CopyOfferSectionsDialog";
import { useCloneOfferMutation } from "../Service/extendedOffersApi";
import { extractApiErrorMessage } from "../../../Utils/apiErrorUtils";
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import { IconButton, Tooltip } from '@mui/material';
import { BookingsRoutes } from "../../../Router/path";

const Offers = () => {
    const [filters, setFilters] = useState<OfferSearchFilters>({
        searchTerm: "",
        hotelId: "",
        categoryId: "",
        countryId: "",
        regionId: "",
        companyId: "",
        published: "",
    });

    const [page, setPage] = useState(0); // zero-based index
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedOffersIds, setSelectedOffersIds] = useState<number[]>([]);
    const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
    const [cloneErrorMessage, setCloneErrorMessage] = useState<string>('');
    const location = useLocation();
    const navigate = useNavigate();

    const { data, isLoading, isError, error } = useGetOffersListNewQuery({
        searchTerm: filters.searchTerm,
        hotelId: filters.hotelId ? parseInt(filters.hotelId) : undefined,
        categoryId: filters.categoryId ? parseInt(filters.categoryId) : undefined,
        countryId: filters.countryId ? parseInt(filters.countryId) : undefined,
        regionId: filters.regionId ? parseInt(filters.regionId) : undefined,
        companyId: filters.companyId ? parseInt(filters.companyId) : undefined,
        onlyPublished:
            filters.published === "" ? undefined : filters.published === "true",
        pageNumber: page + 1, // API 1-based
        pageSize: rowsPerPage,
    });

    const [deleteOffer] = useDeleteOfferMutation();
    const [cloneOffer, cloneOfferStatus] = useCloneOfferMutation();
    const handleCreate = useCreateRedirect(OffersRoutes.create);
    const handleDeleteSelected = useDeleteSelectedItems<number>(
        selectedOffersIds,
        (id) => deleteOffer(id).unwrap(),
        () => setSelectedOffersIds([])
    );

    const columns: ColumnConfig<Offer>[] = [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Nazwa' },
      { key: 'hotelName', label: 'Hotel' },
      { key: 'countryName', label: 'Kraj' },
      { key: 'regionName', label: 'Region' },
      {
        key: 'categoryNames',
        label: 'Kategoria',
        render: (row) => (
          <div>
            {Array.isArray(row.categoryNames) && row.categoryNames.length > 0
              ? row.categoryNames.map((cat, idx) => <div key={idx}>{cat}</div>)
              : '-'}
          </div>
        ),
      },
      { key: 'companyName', label: 'Firma' },
      {
        key: 'published',
        label: 'Opublikowany',
        render: (row) => <input type="checkbox" disabled checked={row.published} />,
      },
    ];

    const selectedOfferId = selectedOffersIds.length === 1 ? selectedOffersIds[0] : null;
    const selectedOffer = data?.items?.find((item) => item.id === selectedOfferId);

    const handleOpenCopyDialog = () => {
        if (selectedOfferId === null) {
            return;
        }

        setCloneErrorMessage('');
        setIsCopyDialogOpen(true);
    };

    const handleConfirmCopy = async (sectionsToCopy: OfferCloneSection[]) => {
        if (selectedOfferId === null) {
            return;
        }

        setCloneErrorMessage('');

        try {
            const response = await cloneOffer({
                sourceOfferId: selectedOfferId,
                data: { sectionsToCopy },
            }).unwrap();

            if (response?.id > 0) {
                setIsCopyDialogOpen(false);
                navigate(OffersRoutes.update.replace(":offerId", response.id.toString()));
            }
        } catch (error) {
            setCloneErrorMessage(
                extractApiErrorMessage(error, 'Nie udało się skopiować oferty. Spróbuj ponownie.')
            );
        }
    };

    return (
        <>
            <ActionButtons
                onCreate={handleCreate}
                onDeleteSelected={handleDeleteSelected}
                onCopySelected={handleOpenCopyDialog}
                isCopyDisabled={selectedOffersIds.length !== 1}
            />
            <OfferSearchForm
                onSearch={(nextFilters) => {
                    setFilters(nextFilters);
                    setPage(0);
                }}
            />

            <DataTable
                columns={columns}
                data={data?.items || []}
                isLoading={isLoading}
                isError={isError}
                errorMessage={extractApiErrorMessage(
                    error,
                    'Nie udało się pobrać listy ofert. Spróbuj ponownie później.'
                )}
                getRowId={(row) => row.id}
                onEdit={(id) => {
                    const item = data?.items.find(item => item.id === id);
                    if (item) {
                        navigate(OffersRoutes.update.replace(":offerId", item.id.toString()));
                    }
                }}
                renderCustomActions={(row) => (
                    <Tooltip title="Dodaj rezerwację">
                        <IconButton
                            size="small"
                            color="primary"
                            sx={{ p: 0.5 }}
                            onClick={() =>
                                navigate(`${BookingsRoutes.create}?offerId=${row.id}`, {
                                    state: {
                                        returnContext: {
                                            pathname: location.pathname,
                                            search: location.search,
                                            label: 'Wróć do listy ofert',
                                        },
                                    },
                                })
                            }
                        >
                            <BookOnlineIcon />
                        </IconButton>
                    </Tooltip>
                )}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={data?.totalCount || 0}
                onPageChange={(newPage) => setPage(newPage)}
                onRowsPerPageChange={(newSize) => {
                    setRowsPerPage(newSize);
                    setPage(0);
                }}
                selected={selectedOffersIds}
                onSelectionChange={(selectedIds) => setSelectedOffersIds(selectedIds)}
            />
            <CopyOfferSectionsDialog
                open={isCopyDialogOpen}
                sourceOfferId={selectedOfferId}
                sourceOfferName={selectedOffer?.name}
                onClose={() => {
                    setCloneErrorMessage('');
                    setIsCopyDialogOpen(false);
                }}
                onConfirm={handleConfirmCopy}
                isLoading={cloneOfferStatus.isLoading}
                errorMessage={cloneErrorMessage}
            />
            <Outlet />
        </>
    );
};

export default Offers;
